import React, { forwardRef } from "react";
import { formatDims } from "../lib/formats";
import { fileUrl } from "../lib/api";
import { fontStack } from "../lib/fonts";

// Renders a single slide. Supports 1:1, 4:5, 9:16 + optional background image + optional brand footer.
const SlideCanvas = forwardRef(({
  slide, index, total, size = 400, format = "1x1", brand = null, exportMode = false,
}, ref) => {
  const dims = formatDims(format);
  const baseW = size;
  const baseH = Math.round(size * (dims.h / dims.w));

  const bg = slide.bg_color || "#F9F8F6";
  const fg = slide.text_color || "#15251E";
  const accent = slide.accent_color || "#244A36";
  const pos = slide.text_position || "center";
  const align = slide.text_align || "left";
  const weight = slide.font_weight || "semibold";
  const sizeKey = slide.font_size || "lg";
  const effect = slide.effect || "none";
  const imgUrl = slide.image_url ? fileUrl(slide.image_url) : "";
  const imgOpacity = typeof slide.image_opacity === "number" ? slide.image_opacity : 0.25;
  const imgPosition = slide.image_position || "background"; // background/top/bottom
  const fontStackCss = fontStack(slide.font_family || "general_sans");

  const posMap = {
    "top-left": "items-start justify-start",
    "top-center": "items-start justify-center",
    "top-right": "items-start justify-end",
    "center-left": "items-center justify-start",
    "center": "items-center justify-center",
    "center-right": "items-center justify-end",
    "bottom-left": "items-end justify-start",
    "bottom-center": "items-end justify-center",
    "bottom-right": "items-end justify-end",
  };

  const weightMap = { normal: 400, medium: 500, semibold: 600, bold: 800 };
  const sizePx = {
    sm: { title: 22, body: 14 },
    md: { title: 28, body: 15 },
    lg: { title: 34, body: 16 },
    xl: { title: 44, body: 17 },
  };
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const s = sizePx[sizeKey] || sizePx.lg;

  // Scale relative to 400px-wide canvas; vertical formats use proportional scaling
  const scaleBase = baseW / 400;

  const padding = 32 * scaleBase;
  const gapStyle = { gap: 12 * scaleBase };

  const hasTopImage = imgUrl && imgPosition === "top";
  const hasBottomImage = imgUrl && imgPosition === "bottom";
  const imageBlockSize = 0.32; // 32% of height for non-background images

  return (
    <div
      ref={ref}
      data-testid={`slide-canvas-${index}`}
      className="slide-surface canvas-shadow relative overflow-hidden"
      style={{
        width: baseW,
        height: baseH,
        background: bg,
        color: fg,
        fontFamily: fontStackCss,
      }}
    >
      {/* Background image */}
      {imgUrl && imgPosition === "background" && (
        <img
          src={imgUrl}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ opacity: imgOpacity }}
        />
      )}

      {/* Effects */}
      {effect === "circle" && (
        <div
          aria-hidden
          className="absolute rounded-full"
          style={{
            width: baseW * 0.8, height: baseW * 0.8,
            right: -baseW * 0.3, top: -baseW * 0.3,
            border: `1px solid ${accent}22`,
            background: `${accent}08`,
          }}
        />
      )}
      {effect === "lines" && (
        <svg aria-hidden width={baseW} height={baseH} className="absolute inset-0 pointer-events-none" style={{ opacity: 0.18 }}>
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={i} x1={0} y1={i * (baseH / 20)} x2={baseW} y2={i * (baseH / 20)} stroke={accent} strokeWidth="0.5" />
          ))}
        </svg>
      )}
      {effect === "grain" && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/></svg>")`,
            mixBlendMode: "multiply",
            opacity: 0.35,
          }}
        />
      )}

      {/* Top image strip */}
      {hasTopImage && (
        <div className="absolute inset-x-0 top-0 overflow-hidden" style={{ height: baseH * imageBlockSize }}>
          <img src={imgUrl} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
        </div>
      )}
      {/* Bottom image strip */}
      {hasBottomImage && (
        <div className="absolute inset-x-0 bottom-0 overflow-hidden" style={{ height: baseH * imageBlockSize }}>
          <img src={imgUrl} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
        </div>
      )}

      {/* Corner decor */}
      <div
        aria-hidden
        className="absolute"
        style={{ top: 20 * scaleBase, left: 20 * scaleBase, width: 32 * scaleBase, height: 1, background: accent, opacity: 0.6, zIndex: 2 }}
      />
      <div
        aria-hidden
        className="absolute"
        style={{
          bottom: 20 * scaleBase, right: 20 * scaleBase,
          padding: `${4 * scaleBase}px ${10 * scaleBase}px`,
          border: `1px solid ${accent}55`, borderRadius: 999,
          fontSize: 10 * scaleBase, color: accent,
          letterSpacing: "0.12em", fontWeight: 600, textTransform: "uppercase",
          zIndex: 2,
        }}
      >
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>

      {/* Content area — adjusted for top/bottom images */}
      <div
        className={`absolute flex flex-col ${posMap[pos] || posMap.center}`}
        style={{
          top: hasTopImage ? baseH * imageBlockSize : 0,
          bottom: hasBottomImage ? baseH * imageBlockSize : 0,
          left: 0, right: 0,
          padding,
          zIndex: 2,
        }}
      >
        <div className={`max-w-full flex flex-col ${alignMap[align]}`} style={gapStyle}>
          {slide.kicker ? (
            <div style={{
              fontSize: 11 * scaleBase,
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: accent, fontWeight: 600,
              fontFamily: fontStackCss,
            }}>{slide.kicker}</div>
          ) : null}
          {slide.title ? (
            <div className={alignMap[align]} style={{
              fontSize: s.title * scaleBase, lineHeight: 1.08,
              letterSpacing: "-0.01em", color: fg, margin: 0,
              fontFamily: fontStackCss,
              fontWeight: weightMap[weight] || 600,
            }}>{slide.title}</div>
          ) : null}
          {slide.body ? (
            <p className={alignMap[align]} style={{
              fontSize: s.body * scaleBase, lineHeight: 1.5, color: `${fg}CC`, margin: 0,
              fontFamily: fontStackCss,
              fontWeight: 400,
            }}>{slide.body}</p>
          ) : null}
          {slide.cta ? (
            <div className={alignMap[align]} style={{ marginTop: 12 * scaleBase }}>
              <span style={{
                display: "inline-block", background: accent, color: bg,
                padding: `${10 * scaleBase}px ${18 * scaleBase}px`,
                borderRadius: 999, fontSize: 13 * scaleBase, fontWeight: 700,
                letterSpacing: "0.02em",
                fontFamily: fontStackCss,
              }}>{slide.cta}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Brand logo (separate position) */}
      {brand && brand.show_on_slides && brand.logo_url && (
        <BrandElement brand={brand} scaleBase={scaleBase} kind="logo" />
      )}

      {/* Brand handle */}
      {brand && brand.show_on_slides && brand.handle && (
        <BrandElement brand={brand} scaleBase={scaleBase} fg={fg} kind="handle" />
      )}

      {/* Brand website */}
      {brand && brand.show_website && brand.website && (
        <BrandElement brand={brand} scaleBase={scaleBase} fg={fg} kind="website" />
      )}
    </div>
  );
});

SlideCanvas.displayName = "SlideCanvas";

function cornerStyle(position, scaleBase) {
  const pad = 20 * scaleBase;
  const style = { position: "absolute", zIndex: 3 };
  const V = (position || "bottom-right").split("-")[0];
  const H = (position || "bottom-right").split("-")[1];
  if (V === "top") style.top = pad;
  else if (V === "bottom") style.bottom = pad;
  else { style.top = "50%"; style.transform = "translateY(-50%)"; }
  if (H === "left") style.left = pad;
  else if (H === "right") style.right = pad;
  else {
    style.left = "50%";
    style.transform = (style.transform ? style.transform + " " : "") + "translateX(-50%)";
  }
  return style;
}

function BrandElement({ brand, scaleBase, fg = "#15251E", kind }) {
  if (kind === "logo") {
    const pos = brand.logo_position || "top-right";
    const sizeKey = brand.logo_size || "md";
    const sizeMap = { sm: 28, md: 40, lg: 56 };
    const px = (sizeMap[sizeKey] || 40) * scaleBase;
    return (
      <img
        src={fileUrl(brand.logo_url)}
        alt=""
        crossOrigin="anonymous"
        style={{ ...cornerStyle(pos, scaleBase), width: px, height: px, borderRadius: 8 * scaleBase, objectFit: "contain" }}
      />
    );
  }
  if (kind === "handle") {
    const pos = brand.handle_position || "bottom-left";
    return (
      <div style={cornerStyle(pos, scaleBase)}>
        <span style={{
          fontSize: 11 * scaleBase, letterSpacing: "0.14em", textTransform: "uppercase",
          fontWeight: 600, color: `${fg}99`,
        }}>{brand.handle}</span>
      </div>
    );
  }
  if (kind === "website") {
    const pos = brand.website_position || "bottom-center";
    const sizeKey = brand.website_size || "sm";
    const fontPx = { sm: 10, md: 12, lg: 14 }[sizeKey] || 10;
    return (
      <div style={cornerStyle(pos, scaleBase)}>
        <span style={{
          fontSize: fontPx * scaleBase, letterSpacing: "0.08em",
          fontWeight: 500, color: `${fg}AA`,
        }}>{brand.website}</span>
      </div>
    );
  }
  return null;
}

export default SlideCanvas;
